<template>
  <Template.Define>
    <ArrowPathIcon
      v-if="pending"
      class="size-4 animate-spin transition"
      :class="[
        variant === 'default' &&
          !isCompact &&
          `text-gray-200 dark:text-muted-foreground`,
      ]"
    />
    <component
      :is="iconMappingMicro[icon]"
      v-else-if="icon"
      class="size-4 transition"
      :class="[
        variant === 'default' &&
          !isCompact &&
          `text-gray-200 dark:text-muted-foreground`,
      ]"
    />

    <span v-if="!isCompact" class="whitespace-nowrap">{{ label }}</span>
  </Template.Define>

  <Tooltip.Root
    :always-show="emphasizeDisablement && !!slots.content"
    :delay-duration="tooltipDelayDuration"
  >
    <Tooltip.Content v-if="slots.content">
      <slot name="content" />
    </Tooltip.Content>
    <Tooltip.Content v-else-if="isCompact">
      {{ label }}
    </Tooltip.Content>

    <Tooltip.Trigger v-if="to">
      <RouterLink
        v-bind="$attrs"
        :to="to"
        :aria-label="label"
        :aria-description="ariaDescription"
        :aria-disabled="disabled"
        :class="classes"
      >
        <Template.Use />
      </RouterLink>
    </Tooltip.Trigger>
    <Tooltip.Trigger v-else>
      <button
        v-bind="$attrs"
        ref="button"
        :type="!formId ? 'button' : 'submit'"
        :form="formId"
        :aria-label="label"
        :aria-description="ariaDescription"
        :aria-disabled="disabled"
        :class="classes"
        @click="!disabled && onClick?.()"
      >
        <Template.Use />
      </button>
    </Tooltip.Trigger>
  </Tooltip.Root>
</template>

<script setup lang="ts">
import { computed, normalizeClass, ref, useTemplateRef } from "vue";
import { RouterLink, useRouter } from "vue-router";

import Tooltip from "@/design_system/Tooltip";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { createReusableTemplate } from "@/lib/composables/template";
import type { KeybindDefinition } from "@/lib/hotkeys";
import { formatKeybind, useKeybinds } from "@/lib/hotkeys";
import type { RouteLocation } from "@/router/types";

import { injectTrayContext } from "./internal/tray-context";

const Template = createReusableTemplate();

const router = useRouter();

const { isCompact } = injectTrayContext({ isCompact: false });

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    label: string;
    description?: string;
    icon: MappedIconMicro;
    variant?: "default" | "primary" | "negative";
    hotkey?: string;
    pending?: boolean;
    disabled?: boolean;
    allowClickingDespiteDisablement?: boolean;
    tooltipDelayDuration?: number;
    formId?: string;
    to?: RouteLocation;
    onClick?: () => void;
  }>(),
  {
    variant: "default",
  }
);

const slots = defineSlots<{
  content?(): unknown;
}>();

const button = useTemplateRef("button");

const classes = computed(() => {
  const variant = props.variant;
  const pending = props.pending;
  const disabled = props.disabled;

  return normalizeClass([
    `flex h-8 items-center gap-2 rounded-md border border-transparent px-2 text-[0.8125rem] font-medium leading-5 outline-2 outline-action transition-colors focus-visible:outline`,
    variant === "default" &&
      "bg-gray-600 text-white hover:bg-gray-700 active:bg-gray-700 dark:border-border dark:bg-muted dark:hover:bg-card dark:active:bg-card",
    variant === "primary" &&
      `bg-green-600 text-white outline-offset-2 hover:bg-green-700 active:bg-green-700`,
    variant === "negative" &&
      "bg-red-600 text-white hover:bg-red-700 active:bg-red-700",
    (pending || disabled) && "cursor-not-allowed opacity-75",
    disabled && emphasizeDisablement.value && "shaking",
  ]);
});

const formattedKeybind = computed(() => {
  const hotkey = props.hotkey;
  return hotkey && formatKeybind(hotkey);
});

const ariaDescription = computed(() => {
  const description = props.description;
  const hotkey = formattedKeybind.value;

  if (description && hotkey) {
    return `${description} (${hotkey})`;
  } else if (description) {
    return description;
  } else if (hotkey) {
    return `Hotkey: ${hotkey}`;
  }

  return undefined;
});

const emphasizeDisablement = ref(false);

useKeybinds(() => {
  const hotkey = props.hotkey;
  if (!hotkey) {
    return;
  }

  const definition: KeybindDefinition = {
    name: props.description ?? props.label,
    group: `Tray actions`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      if (props.disabled) {
        emphasizeDisablement.value = true;

        if (!props.allowClickingDespiteDisablement) {
          return true;
        }
      } else {
        emphasizeDisablement.value = false;
      }

      if (props.to) {
        router.push(props.to);
      } else {
        button.value?.click();
      }

      return true;
    },
  };

  // Tiny edge case; if we have a hotkey of `S`, we want Command+S to work, too.
  if (hotkey === "s") {
    return { s: definition, "$mod+s": { ...definition, hidden: true } };
  }

  return { [hotkey]: definition };
});
</script>
